import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { MapPin, Maximize, Calendar, Heart, Check, Zap, Eye } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { addFavorite, removeFavorite, getCurrentUser } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

const ListingCard = ({ listing, featured = false, compact = false }) => {
  const { isAuthenticated } = useAuth();
  const { toast } = useToast();
  const [isFavorite, setIsFavorite] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      const user = getCurrentUser();
      setIsFavorite(user?.favorites?.includes(listing.id) || false);
    }
  }, [isAuthenticated, listing.id]);

  const handleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      toast({
        title: 'Login vereist',
        description: 'Log in om woningen op te slaan',
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);
    try {
      if (isFavorite) {
        await removeFavorite(listing.id);
        setIsFavorite(false);
        toast({ title: 'Verwijderd', description: 'Woning verwijderd uit favorieten' });
      } else {
        await addFavorite(listing.id);
        setIsFavorite(true);
        toast({ title: 'Opgeslagen!', description: 'Woning toegevoegd aan favorieten' });
      }
      // Update local storage user data
      const user = getCurrentUser();
      if (user) {
        if (isFavorite) {
          user.favorites = (user.favorites || []).filter(id => id !== listing.id);
        } else {
          user.favorites = [...(user.favorites || []), listing.id];
        }
        localStorage.setItem('roomhub_user', JSON.stringify(user));
      }
    } catch (error) {
      toast({
        title: 'Fout',
        description: 'Er ging iets mis. Probeer opnieuw.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const typeConfig = {
    kamer: { label: 'Kamer', gradient: 'from-blue-500 to-indigo-600' },
    studio: { label: 'Studio', gradient: 'from-purple-500 to-pink-600' },
    appartement: { label: 'Appartement', gradient: 'from-amber-500 to-orange-600' },
  };
  const type = typeConfig[listing.type] || typeConfig.kamer;

  if (compact) {
    return (
      <Link to={`/woning/${listing.id}`} className="group block">
        <Card className="overflow-hidden border-0 rounded-2xl bg-white shadow-md hover:shadow-lg transition-shadow">
          <div className="flex">
            <div className="w-24 h-24 flex-shrink-0">
              <img 
                src={listing.images?.[0] || 'https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?w=200'} 
                alt={listing.title} 
                className="w-full h-full object-cover" 
              />
            </div>
            <CardContent className="flex-1 p-3">
              <h3 className="font-bold text-sm text-[#0A1628] line-clamp-1 group-hover:text-[#64DFDF] transition-colors">
                {listing.title}
              </h3>
              <p className="text-xs text-gray-500 flex items-center mt-1">
                <MapPin className="w-3 h-3 mr-1" />
                {listing.city}
              </p>
              <p className="text-lg font-black text-[#0A1628] mt-1">
                €{listing.price}<span className="text-xs font-normal text-gray-500">/mnd {listing.utilitiesIncluded === true ? 'incl.' : listing.utilitiesIncluded === false ? 'excl.' : ''}</span>
              </p>
            </CardContent>
          </div>
        </Card>
      </Link>
    );
  }

  return (
    <Link to={`/woning/${listing.id}`} className="group block" data-testid={`listing-card-${listing.id}`}>
      <Card className={`card-premium overflow-hidden border-0 rounded-3xl bg-white shadow-lg ${featured ? 'ring-2 ring-[#64DFDF]' : ''}`}>
        <div className="relative aspect-[4/3] overflow-hidden">
          <img 
            src={listing.images?.[0] || 'https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?w=600'} 
            alt={listing.title} 
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" 
          />
          
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          
          {/* Badges */}
          <div className="absolute top-4 left-4 flex gap-2">
            {/* Boost Badge */}
            {listing.boosted && (
              <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold px-3 py-1 shadow-lg animate-pulse">
                <Zap className="w-3 h-3 mr-1 fill-white" />BOOST
              </Badge>
            )}
            <Badge className={`bg-gradient-to-r ${type.gradient} text-white font-bold px-3 py-1 shadow-lg`}>
              {type.label}
            </Badge>
            {featured && (
              <Badge className="bg-gradient-to-r from-[#64DFDF] to-[#48BFE3] text-[#0A1628] font-bold px-3 py-1 shadow-lg">
                <Zap className="w-3 h-3 mr-1" />Hot
              </Badge>
            )}
          </div>

          {/* Favorite */}
          <button 
            className={`absolute top-4 right-4 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center transition-all shadow-lg hover:scale-110 hover:bg-white ${isFavorite ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
            onClick={handleFavorite}
            disabled={isLoading}
            data-testid={`favorite-btn-${listing.id}`}
          >
            <Heart 
              className={`w-5 h-5 transition-colors ${isFavorite ? 'text-red-500 fill-red-500' : 'text-gray-600 hover:text-red-500'}`} 
            />
          </button>

          {/* Price */}
          <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-2xl shadow-lg">
            <span className="text-2xl font-black text-[#0A1628]">€{listing.price}</span>
            <span className="text-gray-500 text-sm">/maand</span>
          </div>

          {/* Views */}
          <div className="absolute bottom-4 right-4 bg-black/50 backdrop-blur-sm px-3 py-1 rounded-full flex items-center">
            <Eye className="w-4 h-4 text-white mr-1" />
            <span className="text-white text-sm font-medium">{listing.views}</span>
          </div>
        </div>

        <CardContent className="p-5">
          <h3 className="font-bold text-[#0A1628] mb-2 line-clamp-1 group-hover:text-[#64DFDF] transition-colors text-lg">
            {listing.title}
          </h3>
          <div className="flex items-center text-gray-500 text-sm mb-3">
            <MapPin className="w-4 h-4 mr-1 text-[#64DFDF]" />
            <span className="truncate">{listing.neighborhood}, {listing.city}</span>
          </div>
          
          {/* Property Features Tags */}
          {listing.propertyFeatures && (
            <div className="flex flex-wrap gap-1 mb-3">
              {listing.propertyFeatures.balcony && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Balkon</span>}
              {listing.propertyFeatures.garden && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Tuin</span>}
              {listing.propertyFeatures.parkingIncluded && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Parkeren incl.</span>}
              {listing.propertyFeatures.elevator && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Lift</span>}
              {listing.propertyFeatures.airco && <span className="text-xs bg-cyan-100 text-cyan-700 px-2 py-0.5 rounded-full">Airco</span>}
              {listing.propertyFeatures.internetIncluded && <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">Internet incl.</span>}
              {listing.propertyFeatures.publicTransport && <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">OV dichtbij</span>}
              {listing.propertyFeatures.energyLabel && ['A++++','A+++','A++','A+','A'].includes(listing.propertyFeatures.energyLabel) && (
                <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">{listing.propertyFeatures.energyLabel}</span>
              )}
            </div>
          )}
          
          {/* Utilities info */}
          {listing.utilitiesIncluded !== null && listing.utilitiesIncluded !== undefined && (
            <div className="mb-3">
              <span className={`text-xs px-2 py-0.5 rounded-full ${listing.utilitiesIncluded ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                {listing.utilitiesIncluded ? 'GWL inclusief' : `GWL exclusief${listing.utilitiesCost ? ` (+€${listing.utilitiesCost})` : ''}`}
              </span>
              {listing.serviceCost > 0 && (
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full ml-1">
                  Servicekosten €{listing.serviceCost}
                </span>
              )}
            </div>
          )}
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <div className="flex items-center bg-gray-100 px-3 py-1 rounded-full">
                <Maximize className="w-4 h-4 mr-1" />{listing.size} m²
              </div>
              <div className="flex items-center bg-gray-100 px-3 py-1 rounded-full">
                <Calendar className="w-4 h-4 mr-1" />
                {new Date(listing.available).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}
              </div>
            </div>
          </div>
          {listing.landlord?.verified && (
            <div className="mt-4 pt-4 border-t flex items-center text-sm font-semibold text-[#64DFDF]">
              <div className="w-5 h-5 bg-gradient-to-r from-[#64DFDF] to-[#48BFE3] rounded-full flex items-center justify-center mr-2">
                <Check className="w-3 h-3 text-white" />
              </div>
              Geverifieerde verhuurder
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  );
};

export default ListingCard;
